/**
 * Controllo generico di un campo del modulo ENISA, scelto dal tipo dello schema: radio, testo
 * (textarea oltre 255 caratteri) con contatore, select, multi-select a caselle, data UTC
 * (giorno + ora + minuto). Etichetta ENISA esatta, asterisco sugli obbligatori, errore inline.
 */
import type { ReactNode } from 'react';
import type { EnisaFieldDef, EnisaScalar } from './types.js';
import { composeDate, dateParts, isBlank } from './logic.js';
import styles from './EnisaForm.module.css';

const ORE = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTI = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

/** data-testid di un campo: il percorso con i separatori resi sicuri per i selettori. */
export const fieldTestId = (path: string) => `enisa-field-${path.replace(/[[\].]/g, '-')}`;

export interface EnisaFieldInputProps {
  field: EnisaFieldDef;
  /** Percorso stabile (`title`, `products[0].productType`). */
  path: string;
  value: EnisaScalar | undefined;
  onChange: (value: EnisaScalar) => void;
  /** Mostra l'errore «obbligatorio» se vuoto (dopo un tentativo di salvataggio). */
  showRequired: boolean;
  /** Valore precompilato dal report e non ancora toccato. */
  prefilled: boolean;
  disabled: boolean;
  /** Profondità di annidamento (campi condizionali). */
  depth: number;
}

export function EnisaFieldInput({
  field,
  path,
  value,
  onChange,
  showRequired,
  prefilled,
  disabled,
  depth,
}: EnisaFieldInputProps) {
  const testId = fieldTestId(path);
  const inputId = `enisa-${testId}`;
  const mancante = showRequired && field.required && isBlank(value);
  const testo = typeof value === 'string' ? value : '';
  const troppoLungo = field.maxLength !== undefined && testo.length > field.maxLength;

  let control: ReactNode;
  switch (field.type) {
    case 'radio':
      control = (
        <div className={styles.radioGroup} role="radiogroup" aria-labelledby={`${inputId}-label`}>
          {(field.options ?? []).map((o) => (
            <label key={o.value} className={styles.choice}>
              <input
                type="radio"
                name={inputId}
                value={o.value}
                checked={value === o.value}
                disabled={disabled}
                onChange={() => onChange(o.value)}
                data-testid={`${testId}-${o.value}`}
              />
              {o.label}
            </label>
          ))}
          {!field.required && typeof value === 'string' && value !== '' && (
            <button
              type="button"
              className={styles.linkBtn}
              disabled={disabled}
              onClick={() => onChange('')}
              data-testid={`${testId}-annulla`}
            >
              Annulla scelta
            </button>
          )}
        </div>
      );
      break;
    case 'select':
      control = field.freeText ? (
        <input
          id={inputId}
          className={styles.input}
          value={testo}
          maxLength={field.maxLength}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      ) : (
        <select
          id={inputId}
          className={styles.input}
          value={testo}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        >
          <option value="">— seleziona —</option>
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
      break;
    case 'multiselect': {
      const scelti = Array.isArray(value) ? value : [];
      const opzioni = field.options ?? [];
      control = (
        <div data-testid={testId}>
          <div className={styles.multiActions}>
            <button
              type="button"
              className={styles.linkBtn}
              disabled={disabled}
              onClick={() => onChange(opzioni.map((o) => o.value))}
            >
              Seleziona tutti
            </button>
            <button
              type="button"
              className={styles.linkBtn}
              disabled={disabled}
              onClick={() => onChange([])}
            >
              Nessuno
            </button>
            <span className={styles.muted}>{scelti.length} selezionati</span>
          </div>
          <div className={styles.checkGrid}>
            {opzioni.map((o) => (
              <label key={o.value} className={styles.choice}>
                <input
                  type="checkbox"
                  checked={scelti.includes(o.value)}
                  disabled={disabled}
                  onChange={(e) =>
                    onChange(
                      // Ordine dello schema: lo stesso che usa il server.
                      opzioni
                        .map((x) => x.value)
                        .filter((v) => (v === o.value ? e.target.checked : scelti.includes(v))),
                    )
                  }
                  data-testid={`${testId}-${o.value}`}
                />
                {o.label}
              </label>
            ))}
          </div>
        </div>
      );
      break;
    }
    case 'datetime': {
      const p = dateParts(value);
      const set = (day: string, hour: string, minute: string) =>
        onChange(composeDate(day, hour, minute));
      control = (
        <div className={styles.dateRow} data-testid={testId}>
          <input
            id={inputId}
            type="date"
            className={styles.input}
            value={p.day}
            disabled={disabled}
            onChange={(e) => set(e.target.value, p.hour, p.minute)}
            aria-label="Giorno (UTC)"
            data-testid={`${testId}-giorno`}
          />
          <select
            className={styles.input}
            value={p.hour}
            disabled={disabled || !p.day}
            onChange={(e) => set(p.day, e.target.value, p.minute)}
            aria-label="Ora (UTC)"
            data-testid={`${testId}-ora`}
          >
            {!p.day && <option value="">hh</option>}
            {ORE.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>
          <span>:</span>
          <select
            className={styles.input}
            value={p.minute}
            disabled={disabled || !p.day}
            onChange={(e) => set(p.day, p.hour, e.target.value)}
            aria-label="Minuto (UTC)"
            data-testid={`${testId}-minuto`}
          >
            {!p.day && <option value="">mm</option>}
            {MINUTI.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <span className={styles.muted}>UTC</span>
          {p.day && (
            <button
              type="button"
              className={styles.linkBtn}
              disabled={disabled}
              onClick={() => onChange('')}
            >
              Svuota
            </button>
          )}
        </div>
      );
      break;
    }
    case 'text':
    default: {
      const lungo = (field.maxLength ?? 0) > 255;
      control = lungo ? (
        <textarea
          id={inputId}
          className={styles.textarea}
          value={testo}
          maxLength={field.maxLength}
          rows={4}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      ) : (
        <input
          id={inputId}
          className={styles.input}
          value={testo}
          maxLength={field.maxLength}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      );
    }
  }

  return (
    <div
      className={styles.field}
      style={depth > 0 ? { marginLeft: `${depth * 1.25}rem` } : undefined}
      data-invalid={mancante || troppoLungo || undefined}
      data-path={path}
    >
      <div className={styles.labelRow}>
        <label id={`${inputId}-label`} htmlFor={inputId} className={styles.label}>
          {field.label}
          {field.required && (
            <span className={styles.required} aria-label="obbligatorio">
              {' '}
              *
            </span>
          )}
        </label>
        {prefilled && (
          <span className={styles.prefillBadge} data-testid={`${testId}-precompilato`}>
            precompilato dal report
          </span>
        )}
        {field.maxLength !== undefined && (field.type === 'text' || field.freeText) && (
          <span
            className={troppoLungo ? styles.counterOver : styles.counter}
            data-testid={`${testId}-contatore`}
          >
            {testo.length}/{field.maxLength}
          </span>
        )}
      </div>
      {field.description && <p className={styles.description}>{field.description}</p>}
      {control}
      {mancante && (
        <span className={styles.fieldError} data-testid={`${testId}-errore`}>
          Campo obbligatorio per ENISA
        </span>
      )}
      {troppoLungo && (
        <span className={styles.fieldError}>Massimo {field.maxLength} caratteri</span>
      )}
    </div>
  );
}
